import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Flame, ThumbsUp, Eye, Filter, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Search, ExternalLink, Bookmark, EyeOff, Fuel, Gauge, Calendar, MapPin,
  AlertCircle, Car, ArrowDownRight
} from 'lucide-react';
import { dealApi } from '../services/dealApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPrice, formatMileage } from '../utils/formatters';

// ── Constants ────────────────────────────────────────────────────

const PATTERN_LABELS = {
  sous_cote_valide: 'Sous-cote',
  particulier_brade: 'Particulier brade',
  pepite_cachee: 'Pepite cachee',
  segment_haute_velocite: 'Haute velocite',
  dealer_stock_mort: 'Stock mort',
};

const LEVEL_CONFIG = {
  hot_deal: { label: 'Hot Deal', cls: 'bg-red-500/15 text-red-400 border-red-500/20' },
  good_deal: { label: 'Bon Deal', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20' },
  worth_watching: { label: 'A Surveiller', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/20' },
};

const URGENCY_CONFIG = {
  immediate: { label: 'Urgent', cls: 'bg-red-500/15 text-red-400 border-red-500/20', border: 'border-l-red-500' },
  '24h': { label: '24h', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/20', border: 'border-l-amber-500' },
  negotiate: { label: 'Negociable', cls: 'bg-blue-500/15 text-blue-400 border-blue-500/20', border: 'border-l-blue-500' },
};

const SUMMARY_CARDS = [
  { key: 'hot_deal', Icon: Flame, label: 'Hot Deals', colorCls: 'text-red-400', bgCls: 'bg-red-500/10 border-red-500/20' },
  { key: 'good_deal', Icon: ThumbsUp, label: 'Bons Deals', colorCls: 'text-amber-400', bgCls: 'bg-amber-500/10 border-amber-500/20' },
  { key: 'worth_watching', Icon: Eye, label: 'A Surveiller', colorCls: 'text-blue-400', bgCls: 'bg-blue-500/10 border-blue-500/20' },
];

const PAGE_SIZE = 20;

// ── Inline components ────────────────────────────────────────────

const SellerPill = ({ type }) => {
  const config = type === 'private'
    ? { label: 'Particulier', cls: 'bg-blue-500/10 text-blue-400 border-blue-500/20' }
    : { label: 'Professionnel', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${config.cls}`}>
      {config.label}
    </span>
  );
};

const SpecPill = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center rounded-full bg-dark-hover border border-dark-border px-2 py-0.5 text-[11px] text-secondary">
    {Icon && <Icon className="h-3 w-3 mr-1 text-muted" />}
    {children}
  </span>
);

// ── Summary bar ──────────────────────────────────────────────────

const DealsSummaryBar = ({ summary, activeLevel, onLevelClick }) => {
  const byLevel = summary?.by_level || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {SUMMARY_CARDS.map(({ key, Icon, label, colorCls, bgCls }) => {
        const count = byLevel[key] || 0;
        const isActive = activeLevel === key;
        return (
          <button
            key={key}
            onClick={() => onLevelClick(isActive ? '' : key)}
            className={`flex items-center gap-3 p-4 rounded-xl border transition-colors text-left ${
              isActive
                ? `${bgCls} ring-1 ring-inset ring-current`
                : 'bg-dark-card border-dark-border hover:bg-dark-hover'
            }`}
          >
            <Icon className={`h-6 w-6 ${colorCls}`} />
            <div>
              <p className={`text-2xl font-bold ${colorCls}`}>{count}</p>
              <p className="text-xs text-muted">{label}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ── Deal card ────────────────────────────────────────────────────

const DealCard = ({ deal, onDismiss, onSave }) => {
  const v = deal.vehicle || {};
  const p = deal.pricing || {};
  const analysis = deal.deal_analysis || {};
  const seller = deal.seller || {};
  const market = deal.market_data || {};
  const isDismissed = deal.user_action === 'dismissed';
  const isSaved = deal.user_action === 'saved';

  const urgencyCfg = URGENCY_CONFIG[analysis.urgency] || URGENCY_CONFIG.negotiate;
  const levelCfg = LEVEL_CONFIG[analysis.level] || LEVEL_CONFIG.worth_watching;
  const patternLabel = PATTERN_LABELS[analysis.pattern] || analysis.pattern;

  const scoreColor = analysis.score >= 70 ? 'text-emerald-400' : analysis.score >= 40 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className={`bg-dark-card border border-dark-border rounded-xl overflow-hidden transition-all border-l-4 ${urgencyCfg.border} ${isDismissed ? 'opacity-40' : ''}`}>
      <div className="flex flex-col md:flex-row">

        {/* Left: Image */}
        <div className="relative w-full md:w-48 lg:w-56 shrink-0 aspect-[4/3] md:aspect-auto bg-dark-surface overflow-hidden">
          {v.image_url ? (
            <img
              src={v.image_url}
              alt={`${v.make} ${v.model}`}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[140px]">
              <Car className="h-12 w-12 text-muted/30" />
            </div>
          )}
        </div>

        {/* Middle: Vehicle info */}
        <div className="flex-1 p-4 space-y-3 min-w-0">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="text-base font-semibold text-primary leading-tight">
                {v.make} {v.model} {v.year}
              </h3>
              {p.discount_pct > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-400 border border-emerald-500/20">
                  <ArrowDownRight className="h-3 w-3" />
                  -{Math.round(p.discount_pct)}%
                </span>
              )}
              {patternLabel && (
                <span className="inline-flex items-center rounded-full bg-purple-500/10 border border-purple-500/20 px-2 py-0.5 text-[10px] font-medium text-purple-400">
                  {patternLabel}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
              {seller.type && <SellerPill type={seller.type} />}
              {seller.city && (
                <span className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {seller.city}{seller.zip ? ` (${seller.zip})` : ''}
                </span>
              )}
              {deal.days_online != null && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {deal.days_online}j en ligne
                </span>
              )}
            </div>
          </div>

          {/* Specs */}
          <div className="flex flex-wrap gap-1.5">
            {v.mileage && <SpecPill icon={Gauge}>{formatMileage(v.mileage)}</SpecPill>}
            {v.fuel_type && <SpecPill icon={Fuel}>{v.fuel_type}</SpecPill>}
            {v.body_type && <SpecPill>{v.body_type}</SpecPill>}
            {v.transmission && <SpecPill>{v.transmission}</SpecPill>}
            {v.power_kw && <SpecPill>{v.power_kw} kW</SpecPill>}
          </div>

          {/* Saturation warning */}
          {deal.saturation && deal.saturation.level !== 'none' && (
            <div className="flex items-center gap-1 text-xs text-amber-400 bg-amber-400/10 rounded px-2 py-1 w-fit">
              <AlertCircle className="h-3 w-3" />
              <span>{deal.saturation.similar_count} {deal.saturation.similar_make}{deal.saturation.segment ? ` (${deal.saturation.segment})` : ''} deja en stock</span>
            </div>
          )}

          {/* Reasons */}
          {analysis.reasons && analysis.reasons.length > 0 && (
            <div className="text-xs text-secondary space-y-0.5">
              {analysis.reasons.map((reason, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <span className="text-muted mt-0.5">-</span>
                  <span>{reason}</span>
                </div>
              ))}
            </div>
          )}

          {/* Market data */}
          {(market.similar_count > 0 || market.avg_days_online > 0) && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted">
              {market.similar_count > 0 && (
                <span className="flex items-center gap-1">
                  <Search className="h-3 w-3" />
                  {market.similar_count} similaire{market.similar_count > 1 ? 's' : ''} en ligne
                </span>
              )}
              {market.avg_days_online > 0 && (
                <span>Moy. {market.avg_days_online}j en ligne</span>
              )}
            </div>
          )}
        </div>

        {/* Right: Price + Score + Actions */}
        <div className="w-full md:w-56 lg:w-64 shrink-0 p-4 md:border-l border-t md:border-t-0 border-dark-border flex flex-col gap-4">

          {/* Price block */}
          <div className="bg-dark-hover rounded-lg p-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-[10px] uppercase tracking-wider text-muted">Prix annonce</span>
              <span className="text-base font-bold text-primary">{formatPrice(p.advertised_price)}</span>
            </div>
            {p.estimated_medium > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-wider text-muted">Estimation marche</span>
                <span className="text-sm text-secondary">{formatPrice(p.estimated_medium)}</span>
              </div>
            )}
            {(p.estimated_min > 0 && p.estimated_max > 0) && (
              <div className="flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-wider text-muted">Fourchette</span>
                <span className="text-xs text-muted">{formatPrice(p.estimated_min)} - {formatPrice(p.estimated_max)}</span>
              </div>
            )}
          </div>

          {/* Score + Level + Urgency */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted">Score</span>
              <span className={`text-lg font-bold ${scoreColor}`}>{analysis.score}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${levelCfg.cls}`}>
                {levelCfg.label}
              </span>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${urgencyCfg.cls}`}>
                {urgencyCfg.label}
              </span>
              {analysis.price_trend && (
                <span className="inline-flex items-center rounded-full bg-dark-hover border border-dark-border px-2 py-0.5 text-[10px] text-secondary">
                  {analysis.price_trend}
                </span>
              )}
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-2 mt-auto">
            {deal.url && (
              <a
                href={deal.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 h-9 rounded-lg bg-carmin text-white text-xs font-medium hover:bg-carmin-light transition-colors w-full"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Voir l'annonce
              </a>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => onSave(deal.id)}
                className={`flex-1 h-8 flex items-center justify-center gap-1 rounded-lg border text-xs transition-colors ${
                  isSaved
                    ? 'bg-carmin/10 border-carmin/30 text-carmin'
                    : 'border-dark-border text-muted hover:text-primary'
                }`}
              >
                <Bookmark className="h-3.5 w-3.5" />
                {isSaved ? 'Sauvegarde' : 'Sauvegarder'}
              </button>
              <button
                onClick={() => onDismiss(deal.id)}
                className="h-8 px-3 flex items-center justify-center rounded-lg border border-dark-border text-muted hover:text-red-400 hover:border-red-500/30 text-xs transition-colors"
              >
                <EyeOff className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────

const Deals = () => {
  // Data
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterLevel, setFilterLevel] = useState('');
  const [filterMake, setFilterMake] = useState('');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [filterFuelType, setFilterFuelType] = useState('');
  const [filterBodyType, setFilterBodyType] = useState('');
  const [filterYearMin, setFilterYearMin] = useState('');
  const [filterYearMax, setFilterYearMax] = useState('');
  const [filterScoreMin, setFilterScoreMin] = useState('');
  const [filterSellerType, setFilterSellerType] = useState('');
  const [filterPattern, setFilterPattern] = useState('');
  const [filterUrgency, setFilterUrgency] = useState('');
  const [sortBy, setSortBy] = useState('score');
  const [sortOrder, setSortOrder] = useState('desc');

  const buildParams = useCallback(() => {
    const params = { sort_by: sortBy, sort_order: sortOrder, limit: PAGE_SIZE, offset };
    if (filterLevel) params.level = filterLevel;
    if (filterMake) params.make = filterMake;
    if (filterPriceMin) params.price_min = parseInt(filterPriceMin, 10);
    if (filterPriceMax) params.price_max = parseInt(filterPriceMax, 10);
    if (filterFuelType) params.fuel_type = filterFuelType;
    if (filterBodyType) params.body_type = filterBodyType;
    if (filterYearMin) params.year_min = parseInt(filterYearMin, 10);
    if (filterYearMax) params.year_max = parseInt(filterYearMax, 10);
    if (filterScoreMin) params.score_min = parseInt(filterScoreMin, 10);
    if (filterSellerType) params.seller_type = filterSellerType;
    if (filterPattern) params.pattern = filterPattern;
    if (filterUrgency) params.urgency = filterUrgency;
    return params;
  }, [sortBy, sortOrder, offset, filterLevel, filterMake, filterPriceMin, filterPriceMax, filterFuelType, filterBodyType, filterYearMin, filterYearMax, filterScoreMin, filterSellerType, filterPattern, filterUrgency]);

  const loadDeals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await dealApi.getDeals(buildParams());
      setDeals(result.results || []);
      setTotal(result.total || 0);
    } catch (e) {
      setError('Impossible de charger les deals');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const loadSummary = useCallback(async () => {
    try {
      const result = await dealApi.getSummary();
      setSummary(result);
    } catch (e) {
      // Summary is non-critical
    }
  }, []);

  useEffect(() => {
    loadDeals();
  }, [loadDeals]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleLevelClick = (level) => {
    setFilterLevel(level);
    setOffset(0);
  };

  const handleApplyFilters = () => {
    setOffset(0);
  };

  const handleResetFilters = () => {
    setFilterMake('');
    setFilterPriceMin('');
    setFilterPriceMax('');
    setFilterFuelType('');
    setFilterBodyType('');
    setFilterYearMin('');
    setFilterYearMax('');
    setFilterScoreMin('');
    setFilterSellerType('');
    setFilterPattern('');
    setFilterUrgency('');
    setSortBy('score');
    setSortOrder('desc');
    setFilterLevel('');
    setOffset(0);
  };

  const handleDismiss = async (id) => {
    try {
      await dealApi.dismissDeal(id);
      setDeals(prev => prev.map(d => d.id === id ? { ...d, user_action: 'dismissed' } : d));
    } catch (e) {
      console.error('Dismiss error:', e);
    }
  };

  const handleSave = async (id) => {
    try {
      await dealApi.saveDeal(id);
      setDeals(prev => prev.map(d => d.id === id ? { ...d, user_action: 'saved' } : d));
    } catch (e) {
      console.error('Save error:', e);
    }
  };

  const displayedDeals = useMemo(() => {
    return deals.filter(d => d.user_action !== 'dismissed');
  }, [deals]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const inputCls = 'w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary placeholder-muted focus:outline-none focus:border-carmin/50';
  const selectCls = 'w-full h-9 px-3 rounded-lg bg-dark-hover border border-dark-border text-sm text-primary focus:outline-none focus:border-carmin/50';
  const labelCls = 'block text-[11px] text-muted mb-1';

  return (
    <div className="max-w-7xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary flex items-center gap-2">
            <Flame className="h-6 w-6 text-red-400" />
            Deals
          </h1>
          <p className="text-sm text-muted mt-1">
            Bonnes affaires detectees par l'algorithme
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 h-9 px-3 rounded-lg border text-sm transition-colors ${
            showFilters
              ? 'bg-carmin/10 border-carmin/30 text-carmin'
              : 'border-dark-border text-secondary hover:text-primary'
          }`}
        >
          <Filter className="h-4 w-4" />
          Filtres
          {showFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Summary bar */}
      <DealsSummaryBar summary={summary} activeLevel={filterLevel} onLevelClick={handleLevelClick} />

      {/* Filters bar */}
      {showFilters && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className={labelCls}>Marque</label>
              <input type="text" value={filterMake} onChange={(e) => setFilterMake(e.target.value)} placeholder="Toutes" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prix min</label>
              <input type="number" value={filterPriceMin} onChange={(e) => setFilterPriceMin(e.target.value)} placeholder="0" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Prix max</label>
              <input type="number" value={filterPriceMax} onChange={(e) => setFilterPriceMax(e.target.value)} placeholder="Illimite" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Carburant</label>
              <select value={filterFuelType} onChange={(e) => setFilterFuelType(e.target.value)} className={selectCls}>
                <option value="">Tous</option>
                <option value="essence">Essence</option>
                <option value="diesel">Diesel</option>
                <option value="hybride">Hybride</option>
                <option value="electrique">Electrique</option>
                <option value="gpl">GPL</option>
                <option value="cng">CNG</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Carrosserie</label>
              <input type="text" value={filterBodyType} onChange={(e) => setFilterBodyType(e.target.value)} placeholder="Toutes" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Annee min</label>
              <input type="number" value={filterYearMin} onChange={(e) => setFilterYearMin(e.target.value)} placeholder="Toutes" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Annee max</label>
              <input type="number" value={filterYearMax} onChange={(e) => setFilterYearMax(e.target.value)} placeholder="Toutes" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Score min</label>
              <input type="number" value={filterScoreMin} onChange={(e) => setFilterScoreMin(e.target.value)} placeholder="0" min={0} max={100} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Type vendeur</label>
              <select value={filterSellerType} onChange={(e) => setFilterSellerType(e.target.value)} className={selectCls}>
                <option value="">Tous</option>
                <option value="private">Particulier</option>
                <option value="dealer">Professionnel</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Pattern</label>
              <select value={filterPattern} onChange={(e) => setFilterPattern(e.target.value)} className={selectCls}>
                <option value="">Tous</option>
                {Object.entries(PATTERN_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Urgence</label>
              <select value={filterUrgency} onChange={(e) => setFilterUrgency(e.target.value)} className={selectCls}>
                <option value="">Toutes</option>
                <option value="immediate">Urgent</option>
                <option value="24h">24h</option>
                <option value="negotiate">Negociable</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Trier par</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={selectCls}>
                <option value="score">Score</option>
                <option value="price">Prix</option>
                <option value="days_online">Jours en ligne</option>
                <option value="discount_pct">Reduction</option>
              </select>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-border">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-xs text-secondary">
                <span>Ordre:</span>
                <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="h-7 px-2 rounded bg-dark-hover border border-dark-border text-xs text-primary focus:outline-none focus:border-carmin/50">
                  <option value="desc">Descendant</option>
                  <option value="asc">Ascendant</option>
                </select>
              </label>
              <button onClick={handleResetFilters} className="text-xs text-muted hover:text-secondary transition-colors">
                Reinitialiser
              </button>
            </div>
            <button onClick={handleApplyFilters} className="flex items-center gap-1.5 h-8 px-4 rounded-lg bg-carmin text-white text-xs font-medium hover:bg-carmin-light transition-colors">
              <Search className="h-3.5 w-3.5" />
              Appliquer
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <AlertCircle className="h-4 w-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm text-amber-300">{error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner />
        </div>
      )}

      {/* Empty state */}
      {!loading && displayedDeals.length === 0 && (
        <div className="text-center py-16">
          <Flame className="h-12 w-12 text-muted/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-primary mb-2">Aucun deal trouve</h3>
          <p className="text-sm text-muted max-w-md mx-auto">
            {total === 0
              ? "L'algorithme n'a pas encore detecte de bonnes affaires. Les deals apparaitront une fois l'analyse du marche terminee."
              : "Essayez d'ajuster vos filtres pour elargir la recherche."
            }
          </p>
        </div>
      )}

      {/* Deal list */}
      {!loading && displayedDeals.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted">{total} deal{total > 1 ? 's' : ''} trouve{total > 1 ? 's' : ''}</span>
          </div>

          <div className="space-y-4">
            {displayedDeals.map((deal) => (
              <DealCard
                key={deal.id}
                deal={deal}
                onDismiss={handleDismiss}
                onSave={handleSave}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                disabled={offset === 0}
                className="flex items-center gap-1 h-9 px-3 rounded-lg border border-dark-border text-sm text-secondary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
                Precedent
              </button>
              <span className="text-sm text-muted px-3">
                Page {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PAGE_SIZE)}
                disabled={currentPage >= totalPages}
                className="flex items-center gap-1 h-9 px-3 rounded-lg border border-dark-border text-sm text-secondary hover:text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Suivant
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Deals;
